// Whether a job refresh may forget an area someone removed in CRM on another
// device (#543). Forgetting it drops its photos and anything queued for them
// (device-db.ts forgetArea), so it goes only once nothing of it is still to
// reach CRM or SharePoint from this device, and no photo is being taken in it.
// Until then it stays, and its photos upload as usual: CRM retires an area
// rather than deleting it, so they land in its folder beside its other photos
// (api/src/functions/photos.ts). No device storage here.
import type { DevicePhoto, DeviceRecord, OutboxEntry } from './device-store';

type RecordFacts = Pick<DeviceRecord, 'id' | 'state' | 'parentId' | 'fields'>;
type PhotoFacts = Pick<DevicePhoto, 'id' | 'state' | 'refusedReason'>;

/** Areas a photo is being taken in right now (resized, named, saved), with how many. */
const held = new Map<string, number>();

/**
 * Run `work` (taking a photo in the area) with the area held from its first
 * step to its last: between them nothing of the photo is on the device yet,
 * so only the hold stops a refresh forgetting the area part-way (PR #557 review).
 */
export async function whileAreaHeld<T>(areaId: string, work: () => Promise<T>): Promise<T> {
  held.set(areaId, (held.get(areaId) ?? 0) + 1);
  try {
    return await work();
  } finally {
    const left = (held.get(areaId) ?? 1) - 1;
    if (left) held.set(areaId, left);
    else held.delete(areaId);
  }
}

export const isAreaHeld = (areaId: string): boolean => held.has(areaId);

/** Ids with a change still to reach CRM from this device: anything queued, and any record not synced (pending, in conflict, set aside). */
export function idsWithWork(records: readonly Pick<DeviceRecord, 'id' | 'state'>[], outbox: readonly Pick<OutboxEntry, 'targetId'>[]): Set<string> {
  return new Set([...outbox.map((e) => e.targetId), ...records.filter((r) => r.state !== 'synced').map((r) => r.id)]);
}

/** Still to upload, set aside, or with a change to it or its row (same id) not yet in CRM. */
const photoHasWork = (photo: PhotoFacts, work: ReadonlySet<string>): boolean =>
  photo.state === 'waiting' || !!photo.refusedReason || work.has(photo.id);

/**
 * The area's records: those under it (a tick) or pointing at it (a photo row,
 * its condition visit area), and everything under those in turn (a visit
 * area's ratings and ticks). Only the first step follows a link column; past
 * it, only parents — a link from a job-wide record (the measure's cover photo)
 * never makes that record the area's.
 */
function recordsOfArea(records: readonly RecordFacts[], areaId: string): RecordFacts[] {
  const found = records.filter((r) => r.parentId === areaId || Object.values(r.fields).includes(areaId));
  const ids = new Set(found.map((r) => r.id));
  for (let i = 0; i < found.length; i++) {
    for (const child of records) {
      if (child.parentId === found[i].id && !ids.has(child.id)) {
        ids.add(child.id);
        found.push(child);
      }
    }
  }
  return found;
}

/** What the device holds for the job: the area's photos, and the job's records. */
export interface AreaWork {
  photos: readonly PhotoFacts[];
  records: readonly RecordFacts[];
  work: ReadonlySet<string>;
}

/** The area (removed in CRM) may go from this device: it, its photos and every record of it are in CRM as this device has them. */
export function mayForgetRemovedArea(area: Pick<DeviceRecord, 'id' | 'state'> | undefined, { photos, records, work }: AreaWork): boolean {
  if (!area || area.state !== 'synced' || work.has(area.id)) return false;
  return !photos.some((p) => photoHasWork(p, work)) && !recordsOfArea(records, area.id).some((r) => work.has(r.id));
}
